import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getCookie, QaToolKitOptions } from '@cashier/cashier-common';
import {
  FormStatuses,
  IntegrationThemes,
  PaymentTypes,
} from '@cashier/cashier-model';
import { useUIConfiguration } from '@cashier/cashier-shared';
import { cn, Loader } from '@cashier/cashier-ui';

import { AmountValueProvider, FieldRegistryProvider } from '../contexts';
import {
  useCashierConfiguration,
  useFormAction,
  useFormData,
  useFormStatus,
  usePaymentMethod,
  useSubmittingByEvent,
} from '../hooks';
import { AccountBalance } from '../modules/accountBalance';
import { AccountSelect } from '../modules/accountSelect/v2';
import { AgreementCheckbox } from '../modules/agreementCheckbox';
import { AmountField } from '../modules/amountField/v2';
import { AmountSelector } from '../modules/amountSelector/v2';
import { BonusBanner } from '../modules/bonus';
import { SecuredCardData, StandardCardData } from '../modules/cardData';
import { Description } from '../modules/description';
import { ErrorList } from '../modules/errorList/v2';
import { FieldWrapper } from '../modules/fieldWrapper/v2';
import { SubmitButton } from '../modules/submitButton/v2';

import styles from './styles.module.scss';

interface PaymentFormProps {
  version: 'v2' | 'v3';
}

export const PaymentForm = ({ version }: PaymentFormProps): ReactElement => {
  const status = useFormStatus();

  const { schema, version: formVersion } = useFormData();
  const { paymentType } = useCashierConfiguration();

  useSubmittingByEvent();

  const { onFormSubmitted } = useFormAction();
  const { t, ready: translationsReady } = useTranslation();

  const paymentMethod = usePaymentMethod();

  const errorsPosition = useUIConfiguration('errors', 'position');
  const isBalanceVisible = useUIConfiguration('balance', 'visible');

  const isHostedFieldEnabled =
    getCookie(QaToolKitOptions.enableHostedField) === 'true';

  const { cardData, staticData, commonFieldMap } = schema || {};

  return (
    <div>
      <div
        className={cn({
          [styles.section]: true,
          [styles.sectionV3]: version === 'v3',
        })}
      >
        {status === FormStatuses.INITIATED && (
          <div className={cn([styles.sectionContent, styles.loader])}>
            <div className={styles.loaderContainer}>
              <Loader size="medium" theme="colored" />
            </div>
            <div className={styles.loaderText}>{t('Loading')}</div>
          </div>
        )}

        {status !== FormStatuses.INITIATED && (
          <FieldRegistryProvider>
            <AmountValueProvider
              key={`amountValueProvider_${formVersion}`}
              initialValue={staticData?.amount ? staticData.amount.value : null}
            >
              {staticData?.account && (
                <AccountSelect
                  key={`accountSelect_${formVersion}`}
                  field={staticData.account}
                  version={version}
                />
              )}

              {staticData?.amountSelector && version === 'v2' && (
                <AmountSelector
                  key={`amountSelector_${formVersion}`}
                  field={staticData.amountSelector}
                  version={'v2'}
                />
              )}

              {errorsPosition === 'top' && (
                <div className={styles.errorTopMargin}>
                  <ErrorList />
                </div>
              )}

              <>
                {isBalanceVisible &&
                  paymentType === PaymentTypes.Withdrawal && <AccountBalance />}
                <div
                  className={cn({
                    [styles.sectionContent]: true,
                    [styles.sectionV3]: version === 'v3',
                    [styles.withoutTopMargin]:
                      paymentType === PaymentTypes.Withdrawal,
                  })}
                >
                  <div
                    className={cn({
                      [styles.form]: true,
                      [styles.empty]: !schema,
                      [styles.withoutMargin]:
                        paymentType === PaymentTypes.Withdrawal ||
                        version === 'v3',
                      [styles.bottomMargin]: version === 'v3',
                    })}
                  >
                    <div
                      className={cn({
                        [styles.formField]: true,
                        [styles.emptyBottom]: !schema,
                      })}
                    >
                      <div
                        className={cn({
                          [styles.row]: true,
                          [styles.emptyTop]: !schema,
                          [styles.withoutMargin]:
                            paymentType === PaymentTypes.Withdrawal ||
                            version === 'v3',
                        })}
                      >
                        <div
                          className={cn({
                            [styles.col]: true,
                            [styles.full]: true,
                            [styles.withdrawal]:
                              paymentType === PaymentTypes.Withdrawal,
                            [styles.withdrawalContainer]:
                              paymentType === PaymentTypes.Withdrawal &&
                              isBalanceVisible,
                          })}
                        >
                          {staticData?.amount && (
                            <AmountField
                              key={`amountField_${formVersion}`}
                              field={staticData.amount}
                              version={version}
                            />
                          )}
                        </div>
                      </div>

                      {staticData?.amountSelector && version === 'v3' && (
                        <AmountSelector
                          key={`amountSelector_${formVersion}`}
                          field={staticData.amountSelector}
                          version={'v3'}
                        />
                      )}

                      {errorsPosition === 'bottom' && (
                        <div className={styles.errorBottomMargin}>
                          <ErrorList />
                        </div>
                      )}

                      {paymentMethod.description && (
                        <Description content={paymentMethod.description} />
                      )}

                      {version === 'v2' && <BonusBanner />}

                      {commonFieldMap &&
                        Object.entries(commonFieldMap).map(([_, field]) => (
                          <FieldWrapper
                            key={`field_${field.name}_${formVersion}`}
                            field={field}
                            version={version}
                          />
                        ))}

                      {cardData &&
                        (isHostedFieldEnabled ? (
                          <SecuredCardData
                            key={`cardData_${formVersion}`}
                            schema={cardData}
                          />
                        ) : (
                          <StandardCardData
                            key={`cardData_${formVersion}`}
                            schema={cardData}
                            version={version}
                          />
                        ))}
                    </div>
                  </div>

                  {version === 'v2' && (
                    <SubmitButton onClick={onFormSubmitted} />
                  )}
                </div>
              </>

              {cardData?.termsAgreementToggler && translationsReady && (
                <div
                  className={cn({
                    [styles.checkboxV2]: version === 'v2',
                    [styles.checkboxV3]: version === 'v3',
                  })}
                >
                  <AgreementCheckbox
                    key={`agreementCheckbox_${formVersion}`}
                    field={cardData.termsAgreementToggler}
                    theme={IntegrationThemes.Platform}
                    version={version}
                  />
                </div>
              )}
            </AmountValueProvider>
          </FieldRegistryProvider>
        )}
      </div>
    </div>
  );
};
